import { tForLang } from '../i18n.js';

export function createCreativePalette({ itemDefs, iconProvider, getStackMax, isDurableItemDef, renderSingleSlot, bindTooltip, onMouseDown }) {
  const creativePaletteWrap = document.getElementById('creative-palette-wrap');
  const creativePaletteGrid = document.getElementById('creative-palette-grid');
  const creativeSearchInput = document.getElementById('creative-search-input');
  const creativeSearchEmptyEl = document.getElementById('creative-search-empty');

  const creativeItemIds = Object.keys(itemDefs).sort((a, b) => {
    const aNumeric = /^\d+$/.test(a);
    const bNumeric = /^\d+$/.test(b);
    if (aNumeric && bNumeric) return Number(a) - Number(b);
    if (aNumeric) return -1;
    if (bNumeric) return 1;
    return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
  });

  let creativeSearchQuery = '';

  function normalizeSearchText(text) {
    return String(text || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getItemDisplayNameForLang(def, lang) {
    if (!def) return '';
    const key = `item.${def.id}`;
    const localized = tForLang(lang, key);
    return localized === key ? (def.name || '') : localized;
  }

  function getCreativeSearchTerms(itemId) {
    const def = itemDefs[itemId];
    if (!def) return '';
    return normalizeSearchText([
      itemId,
      def.id || '',
      def.name || '',
      getItemDisplayNameForLang(def, 'zh-TW'),
      getItemDisplayNameForLang(def, 'en')
    ].join(' '));
  }

  function getCreativeStackCount(itemId, single = false) {
    if (single) return 1;
    const def = itemDefs[itemId];
    if (!def) return 0;
    if (def.kind === 'tool' || def.kind === 'armor') return 1;
    return getStackMax(itemDefs, itemId);
  }

  function getDefaultDurabilityForItem(itemId) {
    const def = itemDefs[itemId];
    return isDurableItemDef(def) ? def.maxDurability : null;
  }

  function setupCreativePaletteDom() {
    if (!creativePaletteGrid) return;
    creativePaletteGrid.innerHTML = '';

    for (const itemId of creativeItemIds) {
      const def = itemDefs[itemId];
      if (!def) continue;

      const el = document.createElement('div');
      el.className = 'slot creative-palette-slot';
      el.dataset.itemId = itemId;
      el.dataset.searchTerms = getCreativeSearchTerms(itemId);
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        onMouseDown(itemId, e);
      });
      el.addEventListener('contextmenu', (e) => e.preventDefault());
      bindTooltip(el, () => ({
        id: itemId,
        count: getCreativeStackCount(itemId, false),
        durability: getDefaultDurabilityForItem(itemId)
      }));

      renderSingleSlot(el, {
        id: itemId,
        count: getCreativeStackCount(itemId, false),
        durability: getDefaultDurabilityForItem(itemId)
      });
      creativePaletteGrid.appendChild(el);
    }
  }

  function applyCreativePaletteFilter() {
    if (!creativePaletteGrid) return;
    const normalized = normalizeSearchText(creativeSearchQuery);
    const terms = normalized.length > 0 ? normalized.split(' ').filter(Boolean) : [];
    let visibleCount = 0;

    for (const el of creativePaletteGrid.children) {
      const haystack = el.dataset.searchTerms || '';
      const visible = terms.length === 0 || terms.every((term) => haystack.includes(term));
      el.style.display = visible ? '' : 'none';
      if (visible) visibleCount += 1;
    }

    if (creativeSearchEmptyEl) {
      creativeSearchEmptyEl.style.display = visibleCount > 0 ? 'none' : 'block';
    }
  }

  function renderCreativePalette(craftOpen, creativeMode) {
    if (!creativePaletteGrid) return;
    const show = craftOpen && creativeMode;
    if (creativePaletteWrap) creativePaletteWrap.style.display = show ? 'block' : 'none';
    if (!show) {
      if (creativeSearchEmptyEl) creativeSearchEmptyEl.style.display = 'none';
      return;
    }

    for (const el of creativePaletteGrid.children) {
      const itemId = el.dataset.itemId;
      if (!itemId || !itemDefs[itemId]) continue;
      renderSingleSlot(el, {
        id: itemId,
        count: getCreativeStackCount(itemId, false),
        durability: getDefaultDurabilityForItem(itemId)
      });
    }

    applyCreativePaletteFilter();
  }

  function setupSearchInput(onRender) {
    if (!creativeSearchInput) return;
    creativeSearchInput.addEventListener('input', () => {
      creativeSearchQuery = creativeSearchInput.value || '';
      onRender();
    });
    const stopBubble = (e) => e.stopPropagation();
    creativeSearchInput.addEventListener('keydown', stopBubble);
    creativeSearchInput.addEventListener('keyup', stopBubble);
    creativeSearchInput.addEventListener('compositionstart', stopBubble);
    creativeSearchInput.addEventListener('compositionupdate', stopBubble);
    creativeSearchInput.addEventListener('compositionend', stopBubble);
  }

  function updateLocaleLabels(t) {
    const creativePaletteLabelEl = document.getElementById('creative-palette-label');
    const creativePaletteHintEl = document.getElementById('creative-palette-hint');
    if (creativePaletteLabelEl) creativePaletteLabelEl.innerText = t('ui.creative_palette_label');
    if (creativePaletteHintEl) creativePaletteHintEl.innerText = t('ui.creative_palette_hint');
    if (creativeSearchInput) {
      creativeSearchInput.placeholder = t('ui.creative_search_placeholder');
      if (creativeSearchInput.value !== creativeSearchQuery) {
        creativeSearchInput.value = creativeSearchQuery;
      }
    }
    if (creativeSearchEmptyEl) creativeSearchEmptyEl.innerText = t('ui.creative_search_empty');
  }

  return {
    render: renderCreativePalette,
    setupDom: setupCreativePaletteDom,
    setupSearchInput,
    updateLocaleLabels,
    getCreativeStackCount,
    getDefaultDurabilityForItem
  };
}
